import React, { useState, useEffect, useCallback } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import Classes from './NewPasswordFields.module.scss';
import { PasswordTextField, UtilsBlock, HelperInlineFeedback, SuccessInlineFeedback } from '@hz-design-system/web-ui';

import { MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from '../../../../common/constants';

const NewPasswordComponent = ({
  startValidation,
  onValidationFinished,
  handleSubmit,
}: {
  startValidation: boolean;
  onValidationFinished: Function;
  handleSubmit: Function;
}) => {
  const { t } = useI18nContext();
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLengthValid, setPasswordLengthValid] = useState(false);
  const [passwordHasDigitValid, setPasswordHasDigitValid] = useState(false);
  const [passwordHasSpecCharValid, setPasswordHasSpecCharValid] = useState(false);
  const [passwordValue, setPasswordValue] = useState('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState('');

  const validatePassword = useCallback(() => {
    if (!passwordLengthValid) {
      setPasswordError(t('pages.create_account.validation.password_min_length'));
      return false;
    } else if (!passwordHasDigitValid) {
      setPasswordError(t('pages.create_account.validation.digitNotIncluded'));
      return false;
    } else if (!passwordHasSpecCharValid) {
      setPasswordError(t('pages.create_account.validation.specialCharacterNotIncluded'));
      return false;
    } else if (passwordValue.length > MAX_PASSWORD_LENGTH) {
      setPasswordError(t('pages.create_account.validation.password_max_length'));
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  }, [passwordLengthValid, passwordHasDigitValid, passwordHasSpecCharValid, passwordValue.length, t]);

  const validateConfirmPassword = useCallback(() => {
    if (confirmPasswordValue !== passwordValue) {
      setConfirmPasswordError(t('pages.new_password.validation.password_mismatch'));
      return false;
    } else {
      setConfirmPasswordError('');
      return true;
    }
  }, [confirmPasswordValue, passwordValue, t]);

  useEffect(() => {
    if (startValidation) {
      const isValid = validatePassword() && validateConfirmPassword();
      onValidationFinished(isValid, confirmPasswordValue);
    }
  }, [startValidation, confirmPasswordValue, onValidationFinished, validateConfirmPassword, validatePassword]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPasswordError('');
    setPasswordValue(value);
    setPasswordLengthValid(Boolean(value && value.length >= MIN_PASSWORD_LENGTH));
    setPasswordHasDigitValid(Boolean(value && value.match(/(?=.*[0-9]).{1,}/)));
    setPasswordHasSpecCharValid(Boolean(value && value.match(/(?=.*\W).{1,}/)));
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConfirmPasswordValue(value);
    if (value !== passwordValue) {
      setConfirmPasswordError(t('pages.new_password.validation.password_mismatch'));
    } else {
      setConfirmPasswordError('');
    }
  };

  const onEnterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className={Classes.root}>
      <UtilsBlock pb="s">
        <PasswordTextField
          type="password"
          labelText={t('pages.new_password.new_password_label')}
          aria-label={`${t('pages.new_password.new_password_label')} ${t('pages.new_password.validation.tips.password_min_length')} ${t('pages.new_password.validation.tips.password_includes_digit')} ${t('pages.new_password.validation.tips.password_includes_special_char')}`}
          isRequired={true}
          onChange={handlePasswordChange}
          onKeyPress={onEnterKeyPress}
          showPasswordText={t('pages.new_password.show')}
          hidePasswordText={t('pages.new_password.hide')}
          id="new-password-input"
          errorText={passwordError}
          min={8}
          charsRemainingText={t('pages.create_account.lengthTip')}
          charsExceededText={t('pages.create_account.lengthExceeded')}
          ariaLive="polite"
        />
      </UtilsBlock>
      <UtilsBlock pb="s">
        {passwordLengthValid ? (
          <SuccessInlineFeedback icon="checkmark-circle" className={Classes.successInline}>
            {t('pages.new_password.validation.tips.password_min_length')}
          </SuccessInlineFeedback>
        ) : (
          <HelperInlineFeedback icon="checkmark-circled-line" className={Classes.helperInline}>
            {t('pages.new_password.validation.tips.password_min_length')}
          </HelperInlineFeedback>
        )}
        {passwordHasDigitValid ? (
          <SuccessInlineFeedback icon="checkmark-circle" className={Classes.successInline}>
            {t('pages.new_password.validation.tips.password_includes_digit')}
          </SuccessInlineFeedback>
        ) : (
          <HelperInlineFeedback icon="checkmark-circled-line" className={Classes.helperInline}>
            {t('pages.new_password.validation.tips.password_includes_digit')}
          </HelperInlineFeedback>
        )}
        {passwordHasSpecCharValid ? (
          <SuccessInlineFeedback icon="checkmark-circle" className={Classes.successInline}>
            {t('pages.new_password.validation.tips.password_includes_special_char')}
          </SuccessInlineFeedback>
        ) : (
          <HelperInlineFeedback icon="checkmark-circled-line" className={Classes.helperInline}>
            {t('pages.new_password.validation.tips.password_includes_special_char')}
          </HelperInlineFeedback>
        )}
      </UtilsBlock>
      <UtilsBlock pb="m">
        <PasswordTextField
          type="password"
          labelText={t('pages.new_password.confirm_password_label')}
          isRequired={true}
          onChange={handleConfirmPasswordChange}
          onKeyPress={onEnterKeyPress}
          errorText={confirmPasswordError}
          showPasswordText={t('pages.new_password.show')}
          hidePasswordText={t('pages.new_password.hide')}
          id="confirm-new-password-input"
          value={confirmPasswordValue}
          min={8}
          charsRemainingText={t('pages.create_account.lengthTip')}
          charsExceededText={t('pages.create_account.lengthExceeded')}
          ariaLive="polite"
        />
      </UtilsBlock>
    </div>
  );
};

export default NewPasswordComponent;
